import { NetworkStatus } from '@apollo/client'

import { dataIdFromObject } from '@sketch/graphql-cache'

import {
  GetStashedStorageItemsQuery,
  GetStashedStorageItemsQueryVariables,
  useGetStashedStorageItemsQuery,
} from '@sketch/gql-types'

import { handleFetchMore } from '@sketch/components'
import { ErrorHandler } from '@sketch/tracing'
import { useOnEvent } from '@sketch/utils'

// eslint-disable-next-line no-restricted-imports
import { StorageItemSource, StorageItemType } from '@sketch/gql-types/expansive'

export interface GetStashedStorageItemsProps {
  source: StorageItemSource
  workspaceId: string
  search?: string
  skip?: boolean
  types?: StorageItemType[]
}

const itemsPath = ['workspace', 'storageItems', 'entries']

/**
 * Stashed items are either trashed or archived items.
 */
export const useGetStashedStorageItems = (
  props: GetStashedStorageItemsProps
) => {
  const { workspaceId, search = '', source, types }: typeof props = props

  const variables: GetStashedStorageItemsQueryVariables = {
    source,
    workspaceId,
    after: null,
    search,
    includeAllNestedLevels: false,
    types,
  }

  const { loading, data, error, fetchMore, refetch, networkStatus } =
    useGetStashedStorageItemsQuery({
      // Once we revisit the archive or trash, we want to make sure that we always
      // see the latest data. Therefore, we use `cache-and-network` to ensure that.
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables,
    })

  useOnEvent('workspaceTrashRefresh', ({ workspaceIdentifier }) => {
    if (workspaceIdentifier !== workspaceId || source !== 'TRASH') {
      // Not the workspace visible or not trash - ignore it
      return
    }

    refetch()
  })

  useOnEvent('workspaceShareRefresh', ({ workspaceIdentifier }) => {
    if (workspaceIdentifier !== workspaceId) {
      // Not the workspace visible or not archive - ignore it
      return
    }

    refetch()
  })

  const itemsCount = data?.workspace.storageItems.entries.length

  if (
    (loading && !data?.workspace.storageItems) ||
    // We force the spinner to show when the trash is empty and
    // the "No documents in trash exist" is presented. This makes
    // for a smoother transaction
    //
    // the refetch occurs:
    // - When the trash empty event is triggered
    // - When a document is deleted and the trash list is invalid
    (networkStatus === NetworkStatus.refetch && itemsCount === 0)
  ) {
    return { kind: 'loading' } as const
  }

  if (error) {
    return { kind: 'error', reason: error.message } as const
  }
  if (!data) {
    const reason = 'data should always be truthy in final state' as string
    ErrorHandler.shouldNeverHappen(reason)
    return { kind: 'error', reason } as const
  }

  const { entries, meta } = data.workspace.storageItems
  const { after } = meta

  const totalCounts = {
    storageItems: meta?.totalCount ?? 0,
    shares: meta?.sharesCount ?? 0,
    projects: meta?.nestedProjectsCount ?? 0,
  }

  return {
    kind: 'data',
    items: entries,
    loading,
    totalCounts,
    variables,
    fetchMore: handleFetchMore<
      GetStashedStorageItemsQuery,
      GetStashedStorageItemsQueryVariables
    >(fetchMore, itemsPath, { after, dataIdFromObject }),
  } as const
}
